// Function utilities for the application
import {
  IELTS_TESTS,
  SEARCH_COUNTRY,
  SAVE_MAJOR,
  UNIVERSITIES,
  LEADS,
  APTITUDE_TEST,
} from "../API";
import { authHeaders, syncProfileFromTest, getKnownProfile } from "./studentAuth";
import { addSessionEvent } from "../utils/sessionLog";

// Submit aptitude test results
export const submitAptitudeTest = async (testData) => {
  try {
    const response = await fetch(APTITUDE_TEST, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        // links the attempt to the signed-in ulearn student; {} when anonymous
        ...authHeaders(),
      },
      body: JSON.stringify(testData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    addSessionEvent("test_submitted", {
      test: "aptitude",
      score: testData?.score ?? result?.data?.score ?? null,
    });
    // FILL-ONCE: durable answers from this flow (onboarding + completion form)
    // flow up to the account profile so no later test asks for them again
    syncProfileFromTest({
      ...getKnownProfile(),
      name: testData?.name,
      phone: testData?.number || testData?.phone,
      dob: testData?.dob,
      nationality: testData?.nationality,
      destination: testData?.destination,
      degree: testData?.degree,
    });
    return result;
  } catch (error) {
    console.error("Error submitting aptitude test:", error);
    throw error;
  }
};

// Search countries function
export const searchCountries = async (query) => {
  try {
    // Extract the search text from query object or use query directly if it's a string
    const searchText = typeof query === "object" ? query.text : query;

    const response = await fetch(
      `${SEARCH_COUNTRY}/${encodeURIComponent(searchText)}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error searching countries:", error);
    throw error;
  }
};

export const checkIELTSEligibility = async (eligibilityData) => {
  try {
    const response = await fetch(`${IELTS_TESTS}/eligibility`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(eligibilityData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Eligibility check failed:", response.status, errorText);

      // Handle specific status codes
      if (response.status === 400) {
        throw new Error("Invalid request data. Please check your information.");
      } else if (response.status === 429) {
        throw new Error(
          "You have recently taken a test. Please wait before taking another one.",
        );
      } else if (response.status >= 500) {
        throw new Error("Server error. Please try again later.");
      }

      throw new Error(`Eligibility check failed: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error checking IELTS eligibility:", error);
    // Return a structured error response
    return {
      canTakeTest: false,
      message:
        error.message || "Unable to check eligibility. Please try again.",
      error: true,
    };
  }
};

// Save major function
export const saveMajor = async (majorData) => {
  try {
    console.log(majorData, "majorData");
    const response = await fetch(SAVE_MAJOR, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        // links the attempt to the signed-in ulearn student; {} when anonymous
        ...authHeaders(),
      },
      body: JSON.stringify({ data: majorData }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    addSessionEvent("test_submitted", {
      test: "major",
      qualified: data?.qualified ?? null,
      majors: Array.isArray(majorData?.majors) ? majorData.majors.slice(0, 3) : [],
    });
    // FILL-ONCE: the major flow collects the richest profile (school, GPA,
    // prior English test) — capture all of it
    syncProfileFromTest({
      ...getKnownProfile(),
      name: majorData?.name || majorData?.fullname,
      phone: majorData?.phoneNo
        ? `${majorData.countryCode || ""}${majorData.phoneNo}`
        : "",
      dob: majorData?.dob,
      nationality: majorData?.nationality,
      destination: majorData?.destination,
      degree: majorData?.degree?.en_name || majorData?.degree,
      previousSchool: majorData?.previousSchool,
      cgpa: majorData?.cgpa,
      englishProficiency: majorData?.englishProficiency,
    });
    return data;
  } catch (error) {
    console.error("Error saving major:", error);
    throw error;
  }
};

// Other utility functions can be added here
export const formatDate = (date) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString();
};

export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePhone = (phone) => {
  const phoneRegex = /^[+]?[0-9]{8,15}$/;
  return phoneRegex.test(phone.replace(/\s/g, ""));
};

// Search universities function
export const searchUniversities = async (params) => {
  try {
    const queryParams = new URLSearchParams(params);
    const response = await fetch(`${UNIVERSITIES}?${queryParams}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error searching universities:", error);
    throw error;
  }
};

// Get university function
export const getUniversity = async (id) => {
  try {
    const response = await fetch(`${UNIVERSITIES}/${id}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error getting university:", error);
    throw error;
  }
};

export const createIELTSTest = async (testData) => {
  try {
    // Validate required fields
    const requiredFields = [
      "name",
      "phone",
      "email",
      "scores",
      "answers",
      "startTime",
      "endTime",
    ];
    const missingFields = requiredFields.filter((field) => !testData[field]);

    if (missingFields.length > 0) {
      throw new Error(`Missing required fields: ${missingFields.join(", ")}`);
    }

    // Prepare payload with proper data types
    const payload = {
      // User Information
      name: testData.name.trim(),
      phone: testData.phone.trim(),
      email: testData.email.trim().toLowerCase(),
      dob: testData.dob || undefined,
      nationality: testData.nationality || undefined,
      destination: testData.destination || undefined,
      studyLevel: testData.studyLevel || undefined,
      targetScore: testData.targetScore
        ? parseFloat(testData.targetScore)
        : undefined,

      // Test Information
      testType: testData.testType || "Academic",
      testPurpose: testData.testPurpose || undefined,
      countryCode: testData.countryCode || undefined,
      phoneCode: testData.phoneCode || undefined,

      // Test Results
      scores: {
        reading: testData.scores.reading || {},
        listening: testData.scores.listening || {},
        writing: testData.scores.writing || {},
        overall: testData.scores.overall || {},
        details: testData.scores.details || {},
      },

      // Time Tracking
      timeTaken: testData.timeTaken || {},
      startTime: testData.startTime,
      endTime: testData.endTime,
      completedAt: testData.completedAt,

      // User Answers
      answers: {
        reading: testData.answers.reading || {},
        writing: {
          task1: testData.answers.writing?.task1 || "",
          task2: testData.answers.writing?.task2 || "",
        },
        listening: testData.answers.listening || {},
      },

      // Section Times
      sectionTimes: {
        reading: testData.sectionTimes?.reading || { start: null, end: null },
        writing: testData.sectionTimes?.writing || { start: null, end: null },
        listening: testData.sectionTimes?.listening || {
          start: null,
          end: null,
        },
      },

      // Additional Metadata
      deviceInfo: {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
      },
      ipAddress: testData.ipAddress || undefined,
      referralSource: testData.referralSource || "website",
    };

    console.log("Submitting IELTS test data fn:", payload);

    const response = await fetch(IELTS_TESTS, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        // links the attempt to the signed-in ulearn student; {} when anonymous
        ...authHeaders(),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Test submission failed:", response.status, errorText);

      // Handle specific status codes
      if (response.status === 400) {
        throw new Error("Invalid test data. Please check your information.");
      } else if (response.status === 409) {
        throw new Error("A test with this information already exists.");
      } else if (response.status === 429) {
        throw new Error("Too many requests. Please try again later.");
      } else if (response.status >= 500) {
        throw new Error("Server error. Please try again later.");
      }

      throw new Error(`Test submission failed: ${response.status}`);
    }

    const data = await response.json();
    console.log("Test submitted successfully:", data);
    addSessionEvent("test_submitted", {
      test: "ielts",
      overall: data?.scores?.overall?.bandScore ?? payload?.scores?.overall?.bandScore ?? null,
    });
    // FILL-ONCE: durable answers from onboarding + the end-of-test form
    syncProfileFromTest({
      ...getKnownProfile(),
      name: payload.name,
      email: payload.email,
      phone: payload.phone,
      dob: payload.dob,
      nationality: payload.nationality,
      destination: payload.destination,
      studyLevel: payload.studyLevel,
      targetScore: payload.targetScore,
    });
    return data;
  } catch (error) {
    console.error("Error creating IELTS test:", error);
    throw error;
  }
};

// Create leads function
export const createLeads = async (leadData) => {
  try {
    const response = await fetch(LEADS, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(leadData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error creating lead:", error);
    throw error;
  }
};

// IELTS Registration function
export const createIeltsRegistration = async (formData) => {
  try {
    const payload = {
      name: formData.name,
      age: formData.age.toString(), // Convert to string as per schema
      phoneNumber: formData.phoneNumber,
      email: formData.email.toLowerCase(),
      nationality: formData.nationality,
      studyDestination: formData.studyDestination || "uk", // Default to 'uk' or 'other'
      previousIELTS: formData.previousIELTS || "no", // 'yes' or 'no'
      englishLevel: formData.englishLevel || "average", // 'weak', 'average', 'excellent'
      fieldOfStudy: formData.fieldOfStudy,
      registrationTime: formData.registrationTime,
      guests: formData.guests || [],
    };

    console.log("IELTS Registration Payload:", payload);

    const response = await fetch(
      `${process.env.REACT_APP_API_ENDPOINT_UAPPLY}/ielts-registration`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Server response:", response.status, errorText);
      throw new Error(
        `HTTP error! status: ${response.status}, message: ${errorText}`,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error creating IELTS registration:", error);
    throw error;
  }
};

export const generateId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

export default {
  searchCountries,
  searchUniversities,
  getUniversity,
  createLeads,
  createIeltsRegistration,
  saveMajor,
  formatDate,
  validateEmail,
  validatePhone,
  generateId,
};
